/**
 * Foxline / SoftOne per-slot sync API.
 *
 * Expected stiqos-api surface (HR / Super Admin writes):
 *   GET  /api/softone/work-monitor?store_id&week_start
 *   GET  /api/softone/slot-sync?store_id&week_start
 *   POST /api/softone/slot-sync/refresh
 *   POST /api/softone/slot-sync/fetch-actions
 *   GET  /api/softone/slot-sync/actions
 *   POST /api/softone/slot-sync/first-send
 *   POST /api/softone/slot-sync/upload-blanks
 *   POST /api/softone/slot-sync/replace
 *   POST /api/softone/slot-sync/mark-submitted
 *   POST /api/softone/slot-sync/accept-deletion
 *
 * SoftOne writes never pretend to succeed without an API 2xx.
 */

import { API_BASE, getAuthHeaders } from "./client";
import {
  SLOT_LOG_SOURCE,
  canonicalSlotValue,
  foxlineMonitorRowToCanonical,
  isoToDayKey3,
  slotKey,
} from "../lib/softoneSlotSync";

const LS_PREFIX = "pg_foxline_slot_sync_v1:";

function lsKey(storeId, weekStart) {
  return `${LS_PREFIX}${storeId}:${weekStart}`;
}

function emptyState(storeId, weekStart) {
  return {
    store_id: storeId,
    week_start: weekStart,
    first_sent_at: null,
    monitor_fetched_at: null,
    monitor_available: false,
    persistence: "local",
    slots: [],
    events: [],
  };
}

function readLocalState(storeId, weekStart) {
  try {
    const raw = window.localStorage.getItem(lsKey(storeId, weekStart));
    if (!raw) return emptyState(storeId, weekStart);
    const parsed = JSON.parse(raw);
    return {
      ...emptyState(storeId, weekStart),
      ...parsed,
      persistence: parsed.persistence || "local",
      slots: Array.isArray(parsed.slots) ? parsed.slots : [],
      events: Array.isArray(parsed.events) ? parsed.events : [],
    };
  } catch {
    return emptyState(storeId, weekStart);
  }
}

function writeLocalState(storeId, weekStart, state) {
  try {
    window.localStorage.setItem(lsKey(storeId, weekStart), JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
  return state;
}

async function apiJson(path, options = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: { ...getAuthHeaders(), ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

function isMissingRoute(res) {
  return res.status === 404 || res.status === 501;
}

function upsertSlot(slots, patch) {
  const key = slotKey(patch.employee_code, patch.date);
  const next = [...(slots || [])];
  const idx = next.findIndex((s) => slotKey(s.employee_code, s.date) === key);
  const prev = idx >= 0 ? next[idx] : { employee_code: patch.employee_code, date: patch.date };
  const merged = { ...prev, ...patch };
  if (idx >= 0) next[idx] = merged;
  else next.push(merged);
  return next;
}

function appendLocalEvent(state, event) {
  const row = {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    source: event.source || SLOT_LOG_SOURCE.UPLOAD,
    change_type: event.change_type,
    old_value: event.old_value ?? null,
    new_value: event.new_value ?? null,
    employee_code: event.employee_code,
    employee_name: event.employee_name || "",
    employee_id: event.employee_id ?? null,
    day_key: event.day_key || isoToDayKey3(event.date),
    rota_date: event.date,
    store_name: event.store_name,
    week_start: event.week_start,
    changed_by_name: event.changed_by_name,
    changed_by_role: event.changed_by_role,
    changed_at: event.changed_at || new Date().toISOString(),
    note: event.note || null,
  };
  return { ...state, events: [...(state.events || []), row] };
}

function postChangeLog(event) {
  fetch(`${API_BASE}/schedule/log-change`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      store_name: event.store_name,
      week_start: event.week_start,
      employee_id: event.employee_id ?? event.employee_code,
      employee_code: event.employee_code || null,
      employee_name: event.employee_name || event.employee_code,
      day_key: event.day_key || isoToDayKey3(event.date),
      change_type: event.change_type,
      old_value: event.old_value,
      new_value: event.new_value,
      changed_by_name: event.changed_by_name,
      changed_by_role: event.changed_by_role,
      source: event.source || SLOT_LOG_SOURCE.UPLOAD,
      rota_date: event.date,
    }),
  }).catch((err) => console.error("[schedule/log-change]", err));
}

export function logSlotSyncEvents(events) {
  (events || []).forEach((event) => postChangeLog(event));
}

export async function fetchSlotSyncState(storeId, weekStart, { signal, unifiedLocationId = null } = {}) {
  try {
    const { res, data } = await apiJson(
      `/api/softone/slot-sync?${unifiedLocationId != null ? `unified_location_id=${encodeURIComponent(unifiedLocationId)}` : `store_id=${encodeURIComponent(storeId)}`}&week_start=${encodeURIComponent(weekStart)}`,
      { signal }
    );
    if (res.ok) {
      const state = {
        store_id: storeId,
        week_start: weekStart,
        first_sent_at: data.first_sent_at || null,
        monitor_fetched_at: data.monitor_fetched_at || null,
        monitor_available: data.monitor_available !== false,
        persistence: "api",
        slots: Array.isArray(data.slots) ? data.slots : [],
        events: Array.isArray(data.events) ? data.events : [],
        read_only: Boolean(data.read_only || data.prod_sim),
        prod_sim: Boolean(data.prod_sim),
      };
      writeLocalState(storeId, weekStart, { ...readLocalState(storeId, weekStart), ...state, events: state.events.length ? state.events : readLocalState(storeId, weekStart).events });
      return state;
    }
    if (!isMissingRoute(res) && !res.ok) {
      const err = new Error(typeof data.detail === "string" ? data.detail : `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    if (err.status && err.status !== 404 && err.status !== 501) throw err;
  }
  return readLocalState(storeId, weekStart);
}

export async function fetchMySoftoneSchedule(weekStart, { signal, unifiedLocationId = null } = {}) {
  const params = new URLSearchParams();
  params.set("week_start", weekStart);
  if (unifiedLocationId != null && unifiedLocationId !== "") {
    params.set("unified_location_id", String(unifiedLocationId));
  }
  const { res, data } = await apiJson(`/api/softone/my-schedule?${params.toString()}`, { signal });
  if (!res.ok) {
    const err = new Error(typeof data?.detail === "string" ? data.detail : `SoftOne schedule HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return {
    available: data?.available !== false,
    employee_code: data?.employee_code || "",
    slots: Array.isArray(data?.slots) ? data.slots : [],
  };
}

export async function fetchWorkMonitor(storeId, weekStart, { signal, unifiedLocationId = null } = {}) {
  const { res, data } = await apiJson(
    `/api/softone/work-monitor?${unifiedLocationId != null ? `unified_location_id=${encodeURIComponent(unifiedLocationId)}` : `store_id=${encodeURIComponent(storeId)}`}&week_start=${encodeURIComponent(weekStart)}`,
    { signal }
  );
  if (res.ok) {
    return {
      available: true,
      fetched_at: data.fetched_at || new Date().toISOString(),
      cached: data.cached !== false && data.source === "cache",
      age_seconds: Number.isFinite(Number(data.age_seconds)) ? Number(data.age_seconds) : null,
      source: data.source || (data.cached ? "cache" : "live"),
      slots: Array.isArray(data.slots) ? data.slots : (Array.isArray(data.rows) ? data.rows : []),
      cleared_internal: Array.isArray(data.cleared_internal) ? data.cleared_internal : [],
      read_only: Boolean(data.read_only || data.prod_sim),
      prod_sim: Boolean(data.prod_sim),
      actions_available: Boolean(data.actions_available),
      actions_fetched_at: data.actions_fetched_at || null,
      actions_age_seconds: Number.isFinite(Number(data.actions_age_seconds))
        ? Number(data.actions_age_seconds)
        : null,
    };
  }
  if (isMissingRoute(res)) {
    return { available: false, fetched_at: null, slots: [], missing: true };
  }
  const err = new Error(typeof data.detail === "string" ? data.detail : `Work monitor HTTP ${res.status}`);
  err.status = res.status;
  throw err;
}

function dailyReportFromResponse(data, { cached = false } = {}) {
  return {
    available: data?.available !== false && (Boolean(data?.fetched_at) || Array.isArray(data?.actions)),
    fetched_at: data?.fetched_at || null,
    cached: cached || data?.source === "cache",
    age_seconds: Number.isFinite(Number(data?.age_seconds)) ? Number(data.age_seconds) : null,
    source: data?.source || (cached ? "cache" : "live"),
    actions: Array.isArray(data?.actions) ? data.actions : [],
    stats: data?.stats || null,
    day_errors: Array.isArray(data?.day_errors) ? data.day_errors : [],
    cleared_internal: Array.isArray(data?.cleared_internal) ? data.cleared_internal : [],
  };
}

export async function fetchSoftoneActionsCache(storeId, weekStart, { signal, unifiedLocationId = null } = {}) {
  const { res, data } = await apiJson(
    `/api/softone/slot-sync/actions?${unifiedLocationId != null ? `unified_location_id=${encodeURIComponent(unifiedLocationId)}` : `store_id=${encodeURIComponent(storeId)}`}&week_start=${encodeURIComponent(weekStart)}`,
    { signal }
  );
  if (res.ok) {
    return dailyReportFromResponse(data, { cached: true });
  }
  if (isMissingRoute(res)) {
    return { available: false, fetched_at: null, actions: [], missing: true };
  }
  const err = new Error(typeof data.detail === "string" ? data.detail : `SoftOne actions HTTP ${res.status}`);
  err.status = res.status;
  throw err;
}

export async function fetchSoftoneActions(storeId, weekStart, { unifiedLocationId = null } = {}) {
  const { res, data } = await apiJson("/api/softone/slot-sync/fetch-actions", {
    method: "POST",
    body: JSON.stringify({
      store_id: storeId,
      week_start: weekStart,
      ...(unifiedLocationId != null ? { unified_location_id: unifiedLocationId } : {}),
    }),
  });
  if (res.ok) {
    return dailyReportFromResponse(data, { cached: false });
  }
  if (isMissingRoute(res)) {
    const err = new Error("SoftOne actions API not available");
    err.status = res.status;
    err.missing = true;
    throw err;
  }
  const err = new Error(typeof data.detail === "string" ? data.detail : `Fetch SoftOne actions HTTP ${res.status}`);
  err.status = res.status;
  throw err;
}

export async function refreshWorkMonitor(storeId, weekStart, { unifiedLocationId = null } = {}) {
  const { res, data } = await apiJson("/api/softone/slot-sync/refresh", {
    method: "POST",
    body: JSON.stringify({
      store_id: storeId,
      week_start: weekStart,
      ...(unifiedLocationId != null ? { unified_location_id: unifiedLocationId } : {}),
    }),
  });
  if (res.ok) {
    return {
      available: true,
      fetched_at: data.fetched_at || new Date().toISOString(),
      cached: false,
      age_seconds: Number.isFinite(Number(data.age_seconds)) ? Number(data.age_seconds) : 0,
      source: data.source || "live",
      slots: Array.isArray(data.slots) ? data.slots : [],
      cleared_internal: Array.isArray(data.cleared_internal) ? data.cleared_internal : [],
      actions_available: Boolean(data.actions_available),
      actions_fetched_at: data.actions_fetched_at || null,
      actions_age_seconds: Number.isFinite(Number(data.actions_age_seconds))
        ? Number(data.actions_age_seconds)
        : null,
    };
  }
  if (isMissingRoute(res)) {
    return fetchWorkMonitor(storeId, weekStart);
  }
  const err = new Error(typeof data.detail === "string" ? data.detail : `Refresh HTTP ${res.status}`);
  err.status = res.status;
  throw err;
}

function applySubmittedLocally(state, items, actor, extra = {}) {
  let next = { ...state, slots: [...(state.slots || [])] };
  const now = extra.submitted_at || new Date().toISOString();
  items.forEach((item) => {
    next.slots = upsertSlot(next.slots, {
      employee_code: item.employee_code,
      date: item.date,
      submitted: canonicalSlotValue(item.value),
      submitted_at: now,
      submitted_by: actor,
    });
  });
  if (extra.first_sent_at) next.first_sent_at = extra.first_sent_at;
  return writeLocalState(state.store_id, state.week_start, next);
}

export function submittedSlotItems(slotItems, result) {
  const rows = result?.data?.results;
  if (!Array.isArray(rows)) return slotItems;
  const ok = new Set(
    rows
      .filter((row) => row && row.ok)
      .map((row) => slotKey(row.employee_code, row.date))
  );
  return (slotItems || []).filter((item) => ok.has(slotKey(item.employee_code, item.date)));
}

export async function postFirstSend({ storeId, weekStart, locationId, submittedBy, user, shifts, slotItems, sendEmptyOnly }) {
  const body = {
    store_id: storeId,
    week_start: weekStart,
    location_id: locationId,
    submitted_by: submittedBy,
    send_empty_only: Boolean(sendEmptyOnly),
    shifts,
    slots: slotItems,
  };
  const { res, data } = await apiJson("/api/softone/slot-sync/first-send", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (res.ok) {
    return { ok: true, via: "slot-sync", data };
  }
  const err = new Error(
    isMissingRoute(res)
      ? "SoftOne first-send API is not available yet"
      : typeof data.detail === "string"
        ? data.detail
        : `First send HTTP ${res.status}`
  );
  err.status = res.status;
  err.data = data;
  throw err;
}

export async function postUploadBlanks({ storeId, weekStart, locationId, submittedBy, shifts, slotItems }) {
  const { res, data } = await apiJson("/api/softone/slot-sync/upload-blanks", {
    method: "POST",
    body: JSON.stringify({
      store_id: storeId,
      week_start: weekStart,
      location_id: locationId,
      submitted_by: submittedBy,
      shifts,
      slots: slotItems,
    }),
  });
  if (res.ok) return { ok: true, data };
  if (isMissingRoute(res)) {
    const err = new Error("Blank-only upload API is not available yet");
    err.status = 404;
    throw err;
  }
  const err = new Error(typeof data.detail === "string" ? data.detail : `Upload HTTP ${res.status}`);
  err.status = res.status;
  err.data = data;
  throw err;
}

export async function postReplaceSlots({ storeId, weekStart, locationId, submittedBy, shifts, slotItems }) {
  const { res, data } = await apiJson("/api/softone/slot-sync/replace", {
    method: "POST",
    body: JSON.stringify({
      store_id: storeId,
      week_start: weekStart,
      location_id: locationId,
      submitted_by: submittedBy,
      shifts,
      slots: slotItems,
    }),
  });
  if (res.ok) return { ok: true, data };
  if (isMissingRoute(res)) {
    const err = new Error("SoftOne replace API is not available yet");
    err.status = 404;
    throw err;
  }
  const err = new Error(typeof data.detail === "string" ? data.detail : `Replace HTTP ${res.status}`);
  err.status = res.status;
  err.data = data;
  throw err;
}

/** Persist SoftOne-owned Internal deletes (locked slots). */
export async function postClearSoftoneOwned(storeId, weekStart, { unifiedLocationId = null } = {}) {
  const { res, data } = await apiJson("/api/softone/slot-sync/clear-softone-owned", {
    method: "POST",
    body: JSON.stringify({
      store_id: storeId,
      week_start: weekStart,
      ...(unifiedLocationId != null ? { unified_location_id: unifiedLocationId } : {}),
    }),
  });
  if (res.ok) {
    return {
      ok: true,
      cleared_internal: Array.isArray(data.cleared_internal) ? data.cleared_internal : [],
    };
  }
  if (isMissingRoute(res)) {
    return { ok: false, missing: true, cleared_internal: [] };
  }
  const err = new Error(
    typeof data.detail === "string" ? data.detail : `Clear SoftOne-owned HTTP ${res.status}`
  );
  err.status = res.status;
  throw err;
}

export async function postMarkSubmitted({ storeId, weekStart, slotItems, user }) {
  const { res, data } = await apiJson("/api/softone/slot-sync/mark-submitted", {
    method: "POST",
    body: JSON.stringify({
      store_id: storeId,
      week_start: weekStart,
      slots: slotItems,
      submitted_by: user?.name || user?.id,
    }),
  });
  if (res.ok) return { ok: true, via: "api", data };
  if (!isMissingRoute(res)) {
    const err = new Error(typeof data.detail === "string" ? data.detail : `Mark submitted HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const state = readLocalState(storeId, weekStart);
  const next = applySubmittedLocally(
    state,
    slotItems.map((s) => ({ employee_code: s.employee_code, date: s.date, value: s.value })),
    user?.name || user?.id
  );
  return { ok: true, via: "local", state: next };
}

export async function postAcceptDeletion({ storeId, weekStart, slotItems, user }) {
  const { res, data } = await apiJson("/api/softone/slot-sync/accept-deletion", {
    method: "POST",
    body: JSON.stringify({
      store_id: storeId,
      week_start: weekStart,
      slots: slotItems,
      submitted_by: user?.name || user?.id,
    }),
  });
  if (res.ok) return { ok: true, via: "api", data };
  if (!isMissingRoute(res)) {
    const err = new Error(typeof data.detail === "string" ? data.detail : `Accept deletion HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const state = readLocalState(storeId, weekStart);
  const next = applySubmittedLocally(
    state,
    slotItems.map((s) => ({ employee_code: s.employee_code, date: s.date, value: "" })),
    user?.name || user?.id
  );
  return { ok: true, via: "local", state: next };
}

export function persistLocalAfterSuccessfulSend(storeId, weekStart, slotItems, user, { firstSend } = {}) {
  const state = readLocalState(storeId, weekStart);
  return applySubmittedLocally(
    state,
    slotItems,
    user?.name || user?.id,
    firstSend ? { first_sent_at: new Date().toISOString() } : {}
  );
}

export function persistMonitorSnapshot(storeId, weekStart, monitor) {
  const state = readLocalState(storeId, weekStart);
  let slots = [...(state.slots || [])];
  const fetchedAt = monitor.fetched_at || new Date().toISOString();
  (monitor.slots || []).forEach((row) => {
    const code = row.employee_code || row.employeeCode;
    const date = String(row.date || row.work_date || "").slice(0, 10);
    if (!code || !date) return;
    slots = upsertSlot(slots, {
      employee_code: code,
      date,
      foxline: foxlineMonitorRowToCanonical(row),
      foxline_fetched_at: fetchedAt,
    });
  });
  return writeLocalState(storeId, weekStart, {
    ...state,
    slots,
    monitor_fetched_at: fetchedAt,
    monitor_available: monitor.available !== false,
  });
}

export function persistLocalEvents(storeId, weekStart, events) {
  let state = readLocalState(storeId, weekStart);
  (events || []).forEach((event) => {
    state = appendLocalEvent(state, event);
  });
  return writeLocalState(storeId, weekStart, state);
}

export { readLocalState, writeLocalState };
